import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const API_BASE_URL = '/api/v1';

export enum ObjectPermissionLevel {
  NONE = 'NONE',
  READ = 'READ',
  READ_WRITE = 'READ+WRITE',
}

export enum PagePermissionLevel {
  NONE = 'NONE',
  VIEW = 'VIEW',
  EDIT = 'EDIT',
}

export interface EffectivePermissions {
  objectPermissions: Record<string, ObjectPermissionLevel>;
  pagePermissions: Record<string, PagePermissionLevel>;
  isGlobalAdmin: boolean;
  tenantId: string | null;
}

export function usePermissions() {
  const [permissions, setPermissions] = useState<EffectivePermissions | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPermissions = useCallback(async () => {
    try {
      setLoading(true);
      // Cookie-based auth: withCredentials is set globally in AuthContext
      const response = await axios.get(`${API_BASE_URL}/permissions/me`);
      setPermissions(response.data.data || response.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch permissions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  const checkObjectPermission = useCallback(
    (objectTypeId: number, requiredLevel: ObjectPermissionLevel): boolean => {
      if (!permissions) return false;

      // Global admins have full access to all objects
      if (permissions.isGlobalAdmin) return true;

      const effectiveLevel = permissions.objectPermissions[objectTypeId.toString()];

      if (effectiveLevel === ObjectPermissionLevel.NONE) return false;
      if (effectiveLevel === ObjectPermissionLevel.READ_WRITE) return true;
      return requiredLevel === ObjectPermissionLevel.READ;
    },
    [permissions]
  );

  const checkPagePermission = useCallback(
    (pageId: string, requiredLevel: PagePermissionLevel): boolean => {
      if (!permissions) return false;

      // Global admins have full access to all pages
      if (permissions.isGlobalAdmin) return true;

      const effectiveLevel = permissions.pagePermissions[pageId];

      if (effectiveLevel === PagePermissionLevel.NONE) return false;
      if (effectiveLevel === PagePermissionLevel.EDIT) return true;
      return requiredLevel === PagePermissionLevel.VIEW;
    },
    [permissions]
  );

  const refreshPermissions = useCallback(() => {
    return fetchPermissions();
  }, [fetchPermissions]);

  return {
    permissions,
    loading,
    error,
    isGlobalAdmin: permissions?.isGlobalAdmin || false,
    tenantId: permissions?.tenantId || null,
    checkObjectPermission,
    checkPagePermission,
    refreshPermissions,
  };
}

export function useObjectPermission(objectTypeId: number, requiredLevel: ObjectPermissionLevel) {
  const { permissions, loading } = usePermissions();

  // Calculate permission synchronously based on current permissions state
  const hasPermission = useMemo(() => {
    if (!permissions) return false;

    // Global admins have full access to all objects
    if (permissions.isGlobalAdmin) return true;

    const effectiveLevel = permissions.objectPermissions[objectTypeId.toString()];

    if (effectiveLevel === ObjectPermissionLevel.NONE) return false;
    if (effectiveLevel === ObjectPermissionLevel.READ_WRITE) return true;
    return requiredLevel === ObjectPermissionLevel.READ;
  }, [permissions, objectTypeId, requiredLevel]);

  return { hasPermission, loading };
}

export function usePagePermission(pageId: string, requiredLevel: PagePermissionLevel) {
  const { permissions, loading, error } = usePermissions();

  // Calculate permission synchronously based on current permissions state
  const hasPermission = useMemo(() => {
    if (!permissions) return false;

    // Global admins have full access to all pages
    if (permissions.isGlobalAdmin) return true;

    const effectiveLevel = permissions.pagePermissions[pageId];

    if (effectiveLevel === PagePermissionLevel.NONE) return false;
    if (effectiveLevel === PagePermissionLevel.EDIT) return true;
    return requiredLevel === PagePermissionLevel.VIEW;
  }, [permissions, pageId, requiredLevel]);

  return { hasPermission, loading, error };
}

export function useEffectivePermissions() {
  const { permissions, loading, error } = usePermissions();

  return {
    objectPermissions: permissions?.objectPermissions || {},
    pagePermissions: permissions?.pagePermissions || {},
    isGlobalAdmin: permissions?.isGlobalAdmin || false,
    tenantId: permissions?.tenantId || null,
    loading,
    error,
  };
}
